import React, { useEffect, useState } from 'react';
import BuildTab from './components/BuildTab';
import InsightsTab from './components/InsightsTab';
import { User } from 'common/components/UserAvatar/UserAvatar';
import BEM from 'common/bem';
import mobxInjectSelect from 'common/utils/mobxInjectSelect';

const classes = BEM.with('ClinicalWorkflows');

type Tab = 'build' | 'insights';

type ClinicalWorkflowsProps = {};

type MobxProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  client: Record<string, any> | null;
  currentOrganization: { displayName: string; name: string } | null;
  currentOrganizationId: string;
  currentUser: User | null;
  isClinicalWorkflowBuildEnabled: boolean;
  isClinicalWorkflowInsightsEnabled: boolean;
};

type CombinedProps = ClinicalWorkflowsProps & MobxProps;

export function ClinicalWorkflowsInner({
  client,
  currentOrganization,
  currentOrganizationId,
  currentUser,
  isClinicalWorkflowBuildEnabled,
  isClinicalWorkflowInsightsEnabled,
}: CombinedProps) {
  const defaultTab: Tab = isClinicalWorkflowBuildEnabled ? 'build' : 'insights';
  const [activeTab, setActiveTab] = useState<Tab>(defaultTab);

  useEffect(() => {
    if (
      activeTab === 'build' &&
      !isClinicalWorkflowBuildEnabled &&
      isClinicalWorkflowInsightsEnabled
    ) {
      setActiveTab('insights');
    } else if (
      activeTab === 'insights' &&
      !isClinicalWorkflowInsightsEnabled &&
      isClinicalWorkflowBuildEnabled
    ) {
      setActiveTab('build');
    }
  }, [isClinicalWorkflowBuildEnabled, isClinicalWorkflowInsightsEnabled, activeTab]);

  const showTabs = isClinicalWorkflowBuildEnabled && isClinicalWorkflowInsightsEnabled;

  return (
    <div className={classes()}>
      <div className={classes('header')}>
        <div className={classes('header-icon')}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
          </svg>
        </div>
        <div className={classes('header-text')}>
          <h2 className={classes('title')}>Clinical Workflows</h2>
          <p className={classes('subtitle')}>
            {activeTab === 'insights' && isClinicalWorkflowInsightsEnabled
              ? 'Analytics and reporting'
              : 'Describe your automation in natural language'}
          </p>
        </div>
      </div>

      {showTabs && (
        <div className={classes('nav')} data-test-id="clinical-workflows-nav">
          <div
            className={classes('nav-tab', { selected: activeTab === 'build' })}
            onClick={() => setActiveTab('build')}
            data-test-id="clinical-workflows-build-tab"
          >
            Build
          </div>
          <div
            className={classes('nav-tab', { selected: activeTab === 'insights' })}
            onClick={() => setActiveTab('insights')}
            data-test-id="clinical-workflows-insights-tab"
          >
            Insights
          </div>
        </div>
      )}

      {activeTab === 'insights' && isClinicalWorkflowInsightsEnabled ? (
        <InsightsTab />
      ) : isClinicalWorkflowBuildEnabled ? (
        <BuildTab
          client={client}
          currentOrganization={currentOrganization}
          currentOrganizationId={currentOrganizationId}
          currentUser={currentUser}
        />
      ) : null}
    </div>
  );
}

export default mobxInjectSelect<ClinicalWorkflowsProps, MobxProps>({
  messengerStore: [
    'client',
    'currentOrganization',
    'currentOrganizationId',
    'isClinicalWorkflowBuildEnabled',
    'isClinicalWorkflowInsightsEnabled',
  ],
  sessionStore: ['currentUser'],
})(ClinicalWorkflowsInner);
